import type { AdminOverview } from '@steado/admin-contract/overview';

import { Panel } from '@/components/panel/panels';

interface FunnelPanelProps {
  readonly overview: AdminOverview;
}

const FILLS = ['bg-foreground', 'bg-chart-2', 'bg-primary', 'bg-chart-3'] as const;

/**
 * Signup to paying, as four counts of the same population.
 *
 * Every step is a subset of the one above it, so the bars share a denominator
 * — the total number of accounts — rather than each being a percentage of the
 * step before. A funnel drawn step-to-step makes a small absolute drop late on
 * look like a catastrophe, and the question this answers is "where do people
 * stop", which is an absolute one.
 *
 * The drop line under each step names who did not continue, because that is
 * the actionable half: "2,294 never answered the questions" is a thing to fix,
 * "73% finished onboarding" is a thing to report.
 */
export function FunnelPanel({ overview }: FunnelPanelProps) {
  const total = overview.users.total;

  const steps = [
    { label: 'Signed up', count: total, drop: '' },
    {
      label: 'Finished onboarding',
      count: overview.users.onboarded,
      drop: `${(total - overview.users.onboarded).toLocaleString('en-IN')} never answered the questions`,
    },
    {
      label: 'Has an active plan week',
      count: overview.plans.activeWeeks,
      drop: `${Math.max(0, overview.users.onboarded - overview.plans.activeWeeks).toLocaleString('en-IN')} onboarded but no live plan`,
    },
    {
      label: 'Paying',
      count: overview.billing.premium,
      drop: `${Math.max(0, overview.plans.activeWeeks - overview.billing.premium).toLocaleString('en-IN')} on a plan and still free`,
    },
  ];

  return (
    <Panel title="From signup to paying">
      <div className="flex flex-col">
        {steps.map((step, index) => {
          const share = total === 0 ? 0 : Math.round((step.count / total) * 100);

          return (
            <div key={step.label} className="flex flex-col gap-1.5 py-[7px]">
              <div className="flex items-baseline gap-2">
                <span className="text-[13px] font-medium text-foreground">{step.label}</span>
                <span className="ml-auto font-mono text-[13px] text-foreground">
                  {step.count.toLocaleString('en-IN')}
                </span>
                <span className="w-[52px] text-right font-mono text-[13px] text-muted-foreground">
                  {String(share)}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-secondary">
                <div
                  className={`h-full rounded-full ${FILLS[index] ?? 'bg-primary'}`}
                  style={{ width: `${String(share)}%` }}
                />
              </div>
              {step.drop === '' ? null : <span className="text-xs text-muted-foreground">{step.drop}</span>}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
